import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useGoal } from '../hooks/useGoal'
import { Button } from './ui/button'

const CURRENT_YEAR = new Date().getFullYear()

export function GoalTracker() {
  const { user } = useAuth()
  const { goal, count, loading, error, setGoal } = useGoal(user?.id, CURRENT_YEAR)
  const [editing, setEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    const parsed = parseInt(inputValue, 10)
    if (!parsed || parsed < 1) return
    setSaving(true)
    await setGoal(parsed)
    setSaving(false)
    setEditing(false)
    setInputValue('')
  }

  function handleEdit() {
    setInputValue(goal ? String(goal.target_count) : '')
    setEditing(true)
  }

  function handleCancel() {
    setEditing(false)
    setInputValue('')
  }

  if (loading) {
    return (
      <div
        data-testid="goal-tracker-loading"
        className="mb-8 rounded-lg bg-stone-800 p-5"
      >
        <div className="mb-3 h-4 w-40 rounded bg-stone-700" />
        <div className="h-3 w-full rounded bg-stone-700" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="mb-8 rounded-lg bg-stone-800 p-5">
        <p className="text-sm text-red-400">Failed to load goal: {error}</p>
      </div>
    )
  }

  const isSettingGoal = !goal || editing

  if (isSettingGoal) {
    return (
      <div
        data-testid="goal-tracker-set"
        className="mb-8 rounded-lg border border-stone-700 bg-stone-800/60 p-5"
      >
        <p className="mb-3 font-serif text-base font-semibold text-stone-200">
          Set a goal for {CURRENT_YEAR}
        </p>
        <p className="mb-4 text-sm text-stone-400">
          How many Shakespeare performances do you want to see this year?
        </p>
        <div className="flex items-center gap-3">
          <input
            data-testid="goal-input"
            type="number"
            min={1}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="e.g. 12"
            className="w-24 rounded border border-stone-600 bg-stone-900 px-3 py-1.5 text-sm text-stone-100 placeholder-stone-600 focus:border-stone-400 focus:outline-none"
          />
          <Button
            onClick={handleSave}
            disabled={saving || !inputValue || parseInt(inputValue, 10) < 1}
            className="bg-red-900 px-4 py-1.5 text-sm font-semibold text-stone-100 hover:bg-red-800 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Set goal'}
          </Button>
          {editing && (
            <Button
              variant="ghost"
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm text-stone-400 hover:text-stone-200"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    )
  }

  const pct = Math.min(100, Math.round((count / goal.target_count) * 100))
  const met = count >= goal.target_count

  return (
    <div
      data-testid="goal-tracker-progress"
      className="mb-8 rounded-lg border border-stone-700 bg-stone-800/60 p-5"
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="font-serif text-base font-semibold text-stone-200">
          {CURRENT_YEAR} goal
        </p>
        <button
          data-testid="goal-edit-button"
          onClick={handleEdit}
          className="text-xs text-stone-500 hover:text-stone-300 transition-colors"
          aria-label="Edit goal"
        >
          Edit
        </button>
      </div>

      <p data-testid="goal-progress-text" className="mb-3 text-sm text-stone-300">
        {count} of {goal.target_count}{' '}
        {goal.target_count === 1 ? 'performance' : 'performances'} this year
      </p>

      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${pct}% of goal reached`}
        className="h-2.5 w-full overflow-hidden rounded-full bg-stone-700"
      >
        <div
          data-testid="goal-progress-bar"
          className="h-full rounded-full bg-red-800 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {met && (
        <p
          data-testid="goal-congratulations"
          className="mt-3 text-sm font-medium text-amber-400"
        >
          {count > goal.target_count
            ? `Goal surpassed — ${count - goal.target_count} beyond your target. Bravo!`
            : 'Goal reached. Well played.'}
        </p>
      )}
    </div>
  )
}
